import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Layers, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, apiError, money } from "@/lib/api";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { ConfirmButton } from "@/components/common/ConfirmButton";

function GroupDialog({ kind, group, onClose, onSaved }) {
  const isVar = kind === "variation";
  const [g, setG] = useState({
    name: "", is_required: isVar, is_active: true, options: [{ name: "", price: 0, is_active: true, is_default: false }],
    ...(isVar ? { price_mode: "additional" } : { min_select: 0, max_select: 0 }), ...group,
  });
  const [busy, setBusy] = useState(false);
  const setOpt = (i, k, v) => setG({ ...g, options: g.options.map((o, j) => (j === i ? { ...o, [k]: v } : o)) });
  const url = `/restaurant/${isVar ? "variation" : "addon"}-groups`;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...g, options: g.options.map((o) => ({ ...o, price: Number(o.price) || 0 })) };
    if (!isVar) { payload.min_select = Number(g.min_select) || 0; payload.max_select = Number(g.max_select) || 0; }
    try {
      if (group.id) await api.patch(`${url}/${group.id}`, payload); else await api.post(url, payload);
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="group-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{group.id ? "Edit" : "New"} {isVar ? "variation" : "add-on"} group</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Input required placeholder={isVar ? "e.g. Size" : "e.g. Extra toppings"} value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} data-testid="group-name-input" />
          {isVar ? (
            <div className="grid grid-cols-2 gap-2">
              {[["additional", "Adds to base price"], ["fixed", "Replaces base price"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => setG({ ...g, price_mode: v })} data-testid={`price-mode-${v}`}
                  className={`rounded-xl border px-3 py-2 text-sm transition-colors ${g.price_mode === v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}>{l}</button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">Min select<Input type="number" min="0" value={g.min_select} onChange={(e) => setG({ ...g, min_select: e.target.value })} data-testid="group-min-input" /></label>
              <label className="text-sm">Max select (0 = no limit)<Input type="number" min="0" value={g.max_select} onChange={(e) => setG({ ...g, max_select: e.target.value })} data-testid="group-max-input" /></label>
            </div>
          )}
          <div className="flex gap-6 text-sm">
            <label className="flex items-center gap-2"><Switch checked={g.is_required} onCheckedChange={(v) => setG({ ...g, is_required: v })} data-testid="group-required-switch" />Required</label>
            <label className="flex items-center gap-2"><Switch checked={g.is_active} onCheckedChange={(v) => setG({ ...g, is_active: v })} data-testid="group-active-switch" />Active</label>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Options</p>
            {g.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input required placeholder="Name" value={o.name} onChange={(e) => setOpt(i, "name", e.target.value)} data-testid={`option-name-${i}`} />
                <Input type="number" min="0" step="0.01" className="w-28" placeholder="₹" value={o.price} onChange={(e) => setOpt(i, "price", e.target.value)} data-testid={`option-price-${i}`} />
                {isVar && <label className="flex items-center gap-1 text-xs"><input type="radio" checked={o.is_default} onChange={() => setG({ ...g, options: g.options.map((x, j) => ({ ...x, is_default: j === i })) })} />Default</label>}
                <Switch checked={o.is_active} onCheckedChange={(v) => setOpt(i, "is_active", v)} data-testid={`option-active-${i}`} />
                <button type="button" onClick={() => setG({ ...g, options: g.options.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-rose-600" data-testid={`option-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setG({ ...g, options: [...g.options, { name: "", price: 0, is_active: true, is_default: false }] })} data-testid="option-add-btn"><Plus className="mr-1 h-4 w-4" />Option</Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="group-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GroupList({ kind, items, reload }) {
  const [editing, setEditing] = useState(null);
  const url = `/restaurant/${kind === "variation" ? "variation" : "addon"}-groups`;
  const remove = async (g) => {
    try { await api.delete(`${url}/${g.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <Button className="mb-4 bg-emerald-500 hover:bg-emerald-600" onClick={() => setEditing({})} data-testid={`${kind}-add-btn`}><Plus className="mr-1 h-4 w-4" />New group</Button>
      {items.length === 0 ? <EmptyState icon={Layers} title="Nothing here yet" description="Create a reusable group and attach it to any item." /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((g) => (
            <div key={g.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`${kind}-group-${g.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-semibold text-slate-900">{g.name}</p>
                  <p className="text-xs text-slate-400">
                    {g.is_required ? "Required" : "Optional"}
                    {kind === "variation" ? ` · ${g.price_mode === "fixed" ? "Replaces price" : "Adds to price"}` : ` · pick ${g.min_select}–${g.max_select || "any"}`}
                    {!g.is_active && " · Inactive"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(g)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`${kind}-edit-${g.id}`}><Pencil className="h-4 w-4" /></button>
                  <ConfirmButton title={`Delete "${g.name}"?`} description="It will be removed from every item using it." onConfirm={() => remove(g)} className="p-1 text-slate-400 hover:text-rose-600" testId={`${kind}-delete-${g.id}`}><Trash2 className="h-4 w-4" /></ConfirmButton>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {g.options.map((o) => (
                  <span key={o.id} className={`rounded-full px-2.5 py-1 text-xs ${o.is_active ? "bg-slate-100 text-slate-700" : "bg-slate-50 text-slate-300 line-through"}`}>
                    {o.name} {o.price > 0 && `· ${money(o.price)}`}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <GroupDialog kind={kind} group={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

export default function Customizations() {
  const [data, setData] = useState(null);
  const load = async () => {
    const [a, v] = await Promise.all([api.get("/restaurant/addon-groups"), api.get("/restaurant/variation-groups")]);
    setData({ addons: a.data, variations: v.data });
  };
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;
  return (
    <div>
      <PageHeader title="Add-ons & variations" subtitle="Build once, reuse across many items. Prices are always checked on the server." />
      <Tabs defaultValue="variation">
        <TabsList className="mb-5">
          <TabsTrigger value="variation" data-testid="tab-variations">Variations ({data.variations.length})</TabsTrigger>
          <TabsTrigger value="addon" data-testid="tab-addons">Add-ons ({data.addons.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="variation"><GroupList kind="variation" items={data.variations} reload={load} /></TabsContent>
        <TabsContent value="addon"><GroupList kind="addon" items={data.addons} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
